<template>
  <div class="background-modal">
    <div class="wrapper-modal">
      <div class="wrapper-title">
        <p class="title">Referencia</p>
        <CloseButton @click="closeModal" />
      </div>

      <div class="wrapper-create-sample">
        <div class="wrapper-form">
          <p>Información de la referencia</p>

          <GeneralInput
            v-model:text="reference.val"
            :value="reference.val"
            :label="'Referencia'"
            :has-error="reference.enabled"
            :error="reference.errors.length === 0 ? '' : reference.errors[0]"
            :is-disabled="isEdit"
          />

          <GeneralInput
            v-model:text="description.val"
            :value="description.val"
            :label="'Descripción'"
            :has-error="description.enabled"
            :error="
              description.errors.length === 0 ? '' : description.errors[0]
            "
          />
        </div>

        <div class="wrapper-image">
          <div class="info">
            <p>Imagen principal</p>
            <SecondaryButton
              :buttonLabel="'Subir imagen'"
              @click="$refs.fileImage.click()"
            />
            <input
              type="file"
              ref="fileImage"
              hidden
              @change="uploadImage"
              @click="$event.target.value = ''"
            />
          </div>
          <p class="text">La imagen debe tener una relación 1/1.</p>

          <div class="wrapper-reference-image">
            <figure class="grid-image">
              <img
                v-if="showImagePreview"
                :src="previewImage"
                class="cloth-image"
              />

              <img
                v-else-if="showImage"
                :src="originalReference.main"
                class="cloth-image"
              />

              <p v-else class="no-image">Sin imagen</p>
            </figure>

            <DeleteButton
              v-if="showImagePreview"
              class="delete"
              @click="removeUploadImage"
            />
          </div>
        </div>

        <div class="wrapper-secondary-images" v-if="originalReference != null">
          <div class="info">
            <p>Imágenes de la referencia para el inventario</p>
            <SecondaryButton
              :buttonLabel="'Crear imagen para inventario'"
              @click="inventoryImage(null, null)"
            />
          </div>

          <div v-if="originalReference?.additional != undefined">
            <ul class="secondary-images">
              <li
                class="wrapper-images"
                v-for="(additional, index) in originalReference.additional"
                :key="index"
              >
                <div class="secondary-info">
                  <p>Imagen inventario</p>

                  <div class="secondary-buttons">
                    <EditButton @click="inventoryImage(additional, index)" />
                    <DeleteButton @click="deleteSecondaryReference(index)" />
                  </div>
                </div>

                <img :src="additional.slider" alt="" class="image-secondary" />

                <img :src="additional.full" alt="" class="image-secondary" />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <StandardButton
        :buttonLabel="'Guardar'"
        class="margin-button"
        :is-disabled="disabledForm"
        @click="buildReference"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, Ref, computed } from "vue";
import { createEditSampleValidator } from "@/ux/sampleBook/validators/createEditSampleValidator";
import GeneralInput from "@/components/commons/controls/GeneralInput.vue";
import StandardButton from "@/components/commons/controls/StandardButton.vue";
import SecondaryButton from "@/components/commons/controls/SecondaryButton.vue";
import CloseButton from "@/components/commons/controls/CloseButton.vue";
import EditButton from "@/components/commons/controls/EditButton.vue";
import DeleteButton from "@/components/commons/controls/DeleteButton.vue";
import {
  AdditionalImage,
  Reference,
} from "@/business/inventory/domain/inventory";
import { convertToBase64 } from "@/business/shared/commons/multimedia/convertToBase64";

export default defineComponent({
  name: "ModalCreateEditSample",
  components: {
    GeneralInput,
    StandardButton,
    SecondaryButton,
    CloseButton,
    EditButton,
    DeleteButton,
  },
  props: {
    originalReference: {
      type: Object as () => Reference | null,
      required: false,
    },
  },

  setup(props, context) {
    const { reference, description, successForm, successFormEdit, reset } =
      createEditSampleValidator();

    const isEdit = ref(false);

    const image: Ref<File | null> = ref(null);
    const previewImage: Ref<string> = ref("");
    const showImagePreview: Ref<boolean> = ref(false);

    watch(
      () => props.originalReference,
      () => {
        reset();
        removeUploadImage();

        if (props.originalReference != null) {
          reference.value.val = props.originalReference.reference;
          description.value.val = props.originalReference.description;
          isEdit.value = true;
        } else {
          isEdit.value = false;
        }
      },
      { immediate: true }
    );

    async function uploadImage(event: any) {
      image.value = event.target.files[0];

      if (image.value !== null) {
        previewImage.value = await convertToBase64.execute(image.value);
        showImagePreview.value = true;
      }
    }

    function removeUploadImage(): void {
      previewImage.value = "";
      image.value = null;
      showImagePreview.value = false;
    }

    function closeModal() {
      reset();
      removeUploadImage();
      context.emit("close");
    }

    function inventoryImage(
      additional: AdditionalImage | null,
      position: number | null
    ) {
      context.emit("openInventoryImageModal", additional, position);
    }

    function buildReference() {
      const referenceForm = {
        reference: reference.value.val,
        description: description.value.val,
        image: image.value,
        message: isEdit.value
          ? "Referencia editada correctamente"
          : "Referencia creada correctamente",
      };

      context.emit("buildPrimary", referenceForm);

      removeUploadImage();
    }

    function deleteSecondaryReference(position: number) {
      context.emit("deleteSecondary", position);
    }

    const disabledForm = computed(() => {
      return isEdit.value
        ? successFormEdit.value
        : successForm.value || image.value == null;
    });

    const showImage = computed(() => {
      return isEdit.value && !showImagePreview.value;
    });

    return {
      closeModal,
      inventoryImage,
      buildReference,
      deleteSecondaryReference,
      reference,
      description,
      successForm,
      reset,

      image,
      previewImage,
      showImagePreview,
      uploadImage,
      removeUploadImage,
      disabledForm,
      isEdit,
      showImage,
    };
  },
});
</script>

<style scoped lang="scss">
@import "src/ux/sampleBook/styles/modalCreateEditSampleStyle";
</style>
